if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.purchaseOptions = this.querySelector('[data-dp95-purchase-options]');
        this.selectedPurchaseOption = 'original';
        this.priceContainer = document.getElementById(`price-${this.dataset.sectionId}`);
        this.purchaseOptions?.querySelectorAll('[data-dp95-purchase-option]').forEach((optionButton) => {
          optionButton.addEventListener('click', () => this.setPurchaseOption(optionButton.dataset.dp95PurchaseOption));
        });
        this.updateDisplayedPrice();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);
        const isFramedArtwork =
          this.selectedPurchaseOption === 'framed' &&
          this.purchaseOptions?.dataset.framedVariantId;

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        if (isFramedArtwork) {
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(this.getFramedArtworkPayload(formData));
        } else {
          delete config.headers['Content-Type'];
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (typeof window.dp95UpdateCartIcon === 'function') {
              const itemCount = response.item_count || response.items?.reduce((sum, item) => sum + item.quantity, 0);
              if (itemCount) window.dp95UpdateCartIcon(itemCount);
              fetch(`${routes.cart_url}.js`)
                .then((cartResponse) => cartResponse.json())
                .then((cartState) => window.dp95UpdateCartIcon(cartState.item_count))
                .catch((error) => console.warn('Cart count sync failed', error));
            }
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      setPurchaseOption(option) {
        this.selectedPurchaseOption = option === 'framed' ? 'framed' : 'original';

        this.purchaseOptions?.querySelectorAll('[data-dp95-purchase-option]').forEach((optionButton) => {
          const selected = optionButton.dataset.dp95PurchaseOption === this.selectedPurchaseOption;
          optionButton.classList.toggle('is-selected', selected);
          optionButton.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });

        const propertyInput = this.purchaseOptions?.querySelector('[data-dp95-purchase-property]');
        if (propertyInput) {
          propertyInput.value = this.selectedPurchaseOption === 'framed' ? 'Artist-selected frame' : 'Original artwork';
        }

        const originalLabel = this.submitButton.dataset.originalSubmitLabel || window.variantStrings.addToCart;
        const framedLabel = this.submitButton.dataset.framedSubmitLabel || originalLabel;
        this.submitButtonText.textContent = this.selectedPurchaseOption === 'framed' ? framedLabel : originalLabel;
        this.updateDisplayedPrice();
      }

      updateDisplayedPrice() {
        if (!this.priceContainer || !this.purchaseOptions) return;

        const displayPrice =
          this.selectedPurchaseOption === 'framed'
            ? this.purchaseOptions.dataset.framedDisplayPrice
            : this.purchaseOptions.dataset.originalDisplayPrice;

        if (!displayPrice) return;

        this.priceContainer
          .querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--last')
          .forEach((priceItem) => {
            priceItem.textContent = displayPrice;
          });
      }

      getFramedArtworkPayload(formData) {
        const sections = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [];
        const artworkTitle = this.purchaseOptions?.dataset.productTitle || '';
        const artworkHandle = this.purchaseOptions?.dataset.productHandle || '';
        const artworkUrl = this.purchaseOptions?.dataset.productUrl || '';
        const artworkImageUrl = this.purchaseOptions?.dataset.productImageUrl || '';
        const artworkImageAlt = this.purchaseOptions?.dataset.productImageAlt || artworkTitle;
        const quantity = Number(formData.get('quantity') || 1);

        if (this.purchaseOptions?.dataset.framedMode === 'addon') {
          return {
            items: [
              {
                id: Number(formData.get('id')),
                quantity,
                properties: {
                  '_DP95 purchase option': 'Original artwork',
                },
              },
              {
                id: Number(this.purchaseOptions.dataset.framedVariantId),
                quantity,
                properties: {
                  '_Artwork': artworkTitle,
                  '_Artwork handle': artworkHandle,
                  '_Artwork url': artworkUrl,
                  '_Artwork image': artworkImageUrl,
                  '_Artwork image alt': artworkImageAlt,
                  '_DP95 add-on': 'Artist-selected framing',
                },
              },
            ],
            sections,
            sections_url: window.location.pathname,
          };
        }

        return {
          items: [
            {
              id: Number(this.purchaseOptions.dataset.framedVariantId),
              quantity,
              properties: {
                '_Artwork': artworkTitle,
                '_Artwork handle': artworkHandle,
                '_Artwork url': artworkUrl,
                '_Artwork image': artworkImageUrl,
                '_Artwork image alt': artworkImageAlt,
                '_DP95 purchase option': 'Artist-selected frame',
              },
            },
          ],
          sections,
          sections_url: window.location.pathname,
        };
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          const originalLabel = this.submitButton.dataset.originalSubmitLabel || window.variantStrings.addToCart;
          const framedLabel = this.submitButton.dataset.framedSubmitLabel || originalLabel;
          this.submitButtonText.textContent = this.selectedPurchaseOption === 'framed' ? framedLabel : originalLabel;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
